import { CountryCode } from "libphonenumber-js";
import { allTimezones, TimezoneSelectOptions } from "react-timezone-select";
import allCountries from "world-countries/countries.json";
import { PillVariants } from "../components/pill";
import {
  AdminRoleType,
  AdminValueRoleType,
} from "../redux/features/admin/addAdminTypes";
import { UserType, UserType2 } from "../redux/features/auth/auth.types";
import {
  AvailableCountries,
  CoOrganizerRole,
  EventCategories,
  EventPriceFilterOption,
  EventStatus,
} from "../redux/features/event/event.types";
import { PayoutCurrency } from "../redux/features/payment/payment.types";
import { formatToCurrency } from "./helpers";
import { AdminStatus, EventPermissions, UserStatus } from "./types";

export const API_REQUEST_TIMEOUT = 600000; //milliseconds
export const TICKET_RESERVATION_TIME = 300; //seconds
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
export const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;
export const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
export const clientAuthTokenCookieKey = "tkktAuK";
export const adminAuthTokenCookieKey = "adTkktAuK";
export const salesAuthTokenCookieKey = "saTkktAuK";
export const clientProfileCookieKey = "tkktPK";
export const adminProfileCookieKey = "adTkktPK";
export const salesProfileCookieKey = "saTkktPK";
export const welcomeModalKey = "isWelcomeModalShown";
export const resumeUrlKey = "resumeUrl";
export const OTPMAXTIME = 90;
export const MAX_SIZE_MB = 2 * 1024 * 1024;
export const CLOUDINARY_URL = process.env.REACT_APP_CLOUDINARY_URL;
export const CLOUDINARY_PRESET = "tikket_preset";
export const PRIMARY_COLOR = "#04000A";
export const DEFAULT_EVENT_IMAGE_URL =
  "https://res.cloudinary.com/kneenk/image/upload/v1741163355/tikket_uploads/mw16ijeje8drvsbrrvcj.jpg";
export const PAYSTACK_URL =
  "https://dashboard.paystack.com/#/login?next=app.home.dashboard";
export const WARNING_TICKET_QUANTITY = 10;
export const MAX_PURCHASE_QUANTITY = 5;
export const PRODUCT_PRICING: Record<
  PayoutCurrency,
  {
    feeText: string;
    feePercentage: number;
    fixedFeeCharge: number;
    minTicketPriceForFixedFeeCharge: number;
  }
> = {
  NGN: {
    feeText: "5% + ₦100",
    feePercentage: 0.05,
    fixedFeeCharge: 100,
    minTicketPriceForFixedFeeCharge: 2499,
  },
  "Canadian Dollars": {
    feeText: "3.5% + C$1.29",
    feePercentage: 0.035,
    fixedFeeCharge: 1.29,
    minTicketPriceForFixedFeeCharge: 0,
  },
};
export const SKELETON_BASE_COLOR = "#E5E7EB";
export const SKELETON_HIGHLIGHT_COLOR = "#ffffff";
export const TICKET_COLOR_OPTIONS = [
  "#65007f,#f9eafd",
  "#2051CA,#F0F6FF",
  "#BE0606,#FDF2F2",
  "#CB8E00,#FFF6D6",
  "#008035,#D8F6E4",
  "#697BA7,#F4F7FA",
]; //i.e ["#pillTextColor,#pillBgColor"]

const colorMap = new Map<string, string>();

export const getColorForTicket = (
  ticketName: string,
  usedColors: string[] = [],
): string => {
  // if this ticket already has a color, return it
  if (colorMap.has(ticketName)) {
    return colorMap.get(ticketName)!;
  }
  // gather all colors used
  const globallyUsedColors = Array.from(colorMap.values());
  const availableColors = TICKET_COLOR_OPTIONS.filter(
    (color) =>
      !usedColors.includes(color) && !globallyUsedColors.includes(color),
  );
  // if there are still have unused colors, pick one
  const colorPair =
    availableColors.length > 0
      ? availableColors[Math.floor(Math.random() * availableColors.length)]
      : // if all colors are used reuse from full list
        TICKET_COLOR_OPTIONS[
          Math.floor(Math.random() * TICKET_COLOR_OPTIONS.length)
        ];
  // store it so the same ticket name always gets this color
  colorMap.set(ticketName, colorPair);
  return colorPair;
};

export const guestEventsLSKey = "guestEvents";
export const guestProfileLSKey = "guestProfile";
export const paidTooltip = {
  id: "paid-tooltip",
  text: "This is the total amount we’ve paid into your payout account.",
};
export const pendingPayoutTooltip = {
  id: "pending-payout-tooltip",
  text: `This is how much you’ve made that hasn’t been paid into your payout account.\nN.B. Payouts that fall on weekends and public holidays would be paid the next working day.`,
};

export const domain = window.location.hostname;

export const APP_SCHEME = window.location.protocol;

export const APP_PORT = window.location.port;

export const isLocalhost =
  domain.includes("localhost") || domain.includes("192.");

export const APP_SUBDOMAIN_PREFIX = domain.includes("vercel.app")
  ? "app-"
  : "app.";

export const ADMIN_SUBDOMAIN_PREFIX = domain.includes("vercel.app")
  ? "admin-"
  : "admin.";

export const COOKIE_DOMAIN = domain.includes("usetikket.com")
  ? ".usetikket.com"
  : undefined;

export const nextYear = new Date().getFullYear() + 1;
export const currentYear = new Date().getFullYear();

export const MAIN_DOMAIN = domain
  .replace(APP_SUBDOMAIN_PREFIX, "")
  .replace(ADMIN_SUBDOMAIN_PREFIX, "");

export const APP_SUBDOMAIN = isLocalhost
  ? `${domain}`
  : `${APP_SUBDOMAIN_PREFIX}${domain}`;

export const LANDING_PAGE_URL = isLocalhost
  ? `${APP_SCHEME}//${MAIN_DOMAIN}:${APP_PORT}/`
  : `${APP_SCHEME}//${MAIN_DOMAIN}/`;

export const userStatusPillMap: Record<
  UserStatus,
  { text: string; variant: PillVariants }
> = {
  active: {
    text: "ACTIVE",
    variant: "green",
  },
  inactive: {
    text: "NO ACCOUNT",
    variant: "yellow",
  },
  pending: {
    text: "PENDING",
    variant: "grey",
  },
  suspended: {
    text: "SUSPENDED",
    variant: "yellow",
  },
};

export const coorganizerRolePillMap: Record<
  CoOrganizerRole,
  { text: string; variant: PillVariants }
> = {
  "check-in-only": {
    text: "CHECK-IN ONLY",
    variant: "blue",
  },
  creator: {
    text: "Creator",
    variant: "purple",
  },
  manager: {
    text: "MANAGER",
    variant: "grey",
  },
};

export const adminStatusPillMap: Record<
  AdminStatus,
  { text: string; variant: PillVariants }
> = {
  active: {
    text: "ACTIVE",
    variant: "green",
  },
  inactive: {
    text: "DEACTIVATED",
    variant: "red",
  },
  pending: {
    text: "PENDING",
    variant: "grey",
  },
};

export const eventStatusPillMap: Record<
  EventStatus,
  { text: string; variant: PillVariants }
> = {
  upcoming: {
    text: "UPCOMING",
    variant: "blue",
  },
  draft: {
    text: "DRAFT",
    variant: "grey",
  },
  archived: {
    text: "ARCHIVED",
    variant: "grey",
  },
  completed: {
    text: "PAST",
    variant: "green",
  },
  cancelled: {
    text: "CANCELLED",
    variant: "red",
  },
  flagged: {
    text: "FLAGGED",
    variant: "red",
  },
};

export const userTypePillMap: Record<
  UserType | UserType2,
  { text: string; variant: PillVariants }
> = {
  organiser: {
    text: "Organizer",
    variant: "purple",
  },
  guest: {
    text: "Guest",
    variant: "grey",
  },
  attendee: {
    text: "Guest",
    variant: "grey",
  },
};

export const eventCategories: {
  name: EventCategories;
  value: EventCategories;
}[] = [
  {
    name: "General",
    value: "General",
  },
  {
    name: "Kids & Teens",
    value: "Kids & Teens",
  },
  {
    name: "Fashion",
    value: "Fashion",
  },
  {
    name: "Media & Film",
    value: "Media & Film",
  },
  {
    name: "Sports & Fitness",
    value: "Sports & Fitness",
  },
  {
    name: "Government & Politics",
    value: "Government & Politics",
  },
  {
    name: "Community",
    value: "Community",
  },
  {
    name: "Charity & Causes",
    value: "Charity & Causes",
  },
  {
    name: "Education",
    value: "Education",
  },
  {
    name: "Concert",
    value: "Concert",
  },
  {
    name: "Career",
    value: "Career",
  },
  {
    name: "Religion & Spirituality",
    value: "Religion & Spirituality",
  },
  {
    name: "Finance & Investment",
    value: "Finance & Investment",
  },
  {
    name: "Business",
    value: "Business",
  },
  {
    name: "Party",
    value: "Party",
  },
  {
    name: "Tech & Science",
    value: "Tech & Science",
  },
  {
    name: "Startups",
    value: "Startups",
  },
  {
    name: "Food & Drinks",
    value: "Food & Drinks",
  },
  {
    name: "Arts & Culture",
    value: "Arts & Culture",
  },
  {
    name: "Music & Entertainment",
    value: "Music & Entertainment",
  },
];

export const timezones = {
  ...allTimezones,
  "Europe/Berlin": "Frankfurt",
  "Africa/Lagos": "Lagos",
};

export const tzOptions: TimezoneSelectOptions = {
  labelStyle: "abbrev",
  timezones: timezones,
  displayValue: "UTC",
};

export const clientTZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

export const currencyOptions: {
  name: string;
  value: PayoutCurrency;
}[] = [
  {
    name: "Nigerian Naira",
    value: "NGN",
  },
  // {
  //   name: "US Dollars",
  //   value: "US Dollars",
  // },
  {
    name: "Canadian Dollars",
    value: "Canadian Dollars",
  },
];

export const currencyShorts: Record<PayoutCurrency, string> = {
  "Canadian Dollars": "CAD",
  NGN: "NGN",
  // "US Dollars": "USD",
};

export const currencyFull: Record<PayoutCurrency, string> = {
  "Canadian Dollars": "Canadian Dollars",
  NGN: "Nigerian Naira",
  // "US Dollars": "US Dollars",
};

export const currencyFlags: Record<PayoutCurrency, string> = {
  "Canadian Dollars": "🇨🇦",
  NGN: "🇳🇬",
  // "US Dollars": "US Dollars",
};

export const FindEventsCategoryOptions: {
  icon: string;
  iconClassName: string;
  text: string;
  value?: "free" | EventCategories;
}[] = [
  {
    icon: "calendar",
    iconClassName: "bg-[#49CA83]",
    text: "All",
    value: undefined,
  },
  {
    icon: "sparkle",
    iconClassName: "bg-[#FC800C]",
    text: "Free",
    value: "free",
  },
  {
    icon: "music",
    iconClassName: "bg-[#9939FF]",
    text: "Concerts",
    value: "Concert",
  },
  {
    icon: "party-popper",
    iconClassName: "bg-[#3988FF]",
    text: "Parties",
    value: "Party",
  },
  {
    icon: "hand-platter",
    iconClassName: "bg-[#FF3838]",
    text: "Foodie",
    value: "Food & Drinks",
  },
  {
    icon: "briefcase",
    iconClassName: "bg-[#881414]",
    text: "Career",
    value: "Career",
  },
];

export const currencyPriceFilters: Record<
  AvailableCountries,
  { title: string; value: EventPriceFilterOption }[]
> = {
  Nigeria: [
    {
      title: `${formatToCurrency(5000, { currency: "NGN" })} - ${formatToCurrency(10000, { currency: "NGN" })}`,
      value: "5000-10999",
    },
    {
      title: `${formatToCurrency(10000, { currency: "NGN" })} - ${formatToCurrency(20000, { currency: "NGN" })}`,
      value: "11000-25000",
    },
    {
      title: `${formatToCurrency(25000, { currency: "NGN" })}+`,
      value: "25000+",
    },
  ],
  Canada: [
    {
      title: `${formatToCurrency(10, { currency: "CAD" })} - ${formatToCurrency(20, { currency: "CAD" })}`,
      value: "10-20",
    },
    {
      title: `${formatToCurrency(20, { currency: "CAD" })} - ${formatToCurrency(30, { currency: "CAD" })}`,
      value: "20-30",
    },
    {
      title: `${formatToCurrency(30, { currency: "CAD" })} - ${formatToCurrency(40, { currency: "CAD" })}`,
      value: "30-40",
    },
    {
      title: `${formatToCurrency(40, { currency: "CAD" })} - ${formatToCurrency(50, { currency: "CAD" })}`,
      value: "40-50",
    },
    {
      title: `${formatToCurrency(50, { currency: "CAD" })}+`,
      value: "50+",
    },
  ],
};

export const eventCreatorPermissions: EventPermissions[] = [
  "checkInGuests",
  "deleteEvent",
  "duplicateEvent",
  "editEventDetails",
  "addTicket",
  "deleteTicket",
  "editTicketDetails",
  "addCoorganizers",
  "manageCoorganizers",
  "cancelEvent",
  "archiveEvent",
  "assignDiscounts",
  "sendEmailBlasts",
  "viewCoorganizers",
  "viewGuestList",
  "viewSales",
];

export const eventManagerPermissions: EventPermissions[] = [
  "checkInGuests",
  "deleteEvent",
  "duplicateEvent",
  "editEventDetails",
  "addTicket",
  "deleteTicket",
  "editTicketDetails",
  // "addCoorganizers",
  // "manageCoorganizers",
  "cancelEvent",
  "archiveEvent",
  "assignDiscounts",
  "sendEmailBlasts",
  "viewCoorganizers",
  "viewGuestList",
  "viewSales",
];

export const eventCheckInPermissions: EventPermissions[] = [
  "checkInGuests",
  "viewGuestList",
];

export const adminRoles: { name: AdminRoleType; value: AdminValueRoleType }[] =
  [
    { name: "Admin", value: "admin" },
    { name: "Super Admin", value: "super-admin" },
  ];

export const accountIcons: Record<PayoutCurrency, string> = {
  NGN: "nigeria",
  "Canadian Dollars": "canada",
  // "US Dollars": "us",
};

export const COUNTRIES: Record<
  string,
  {
    name: string;
    flag: string;
    countryCode: string;
    nationality: string;
  }
> = Object.fromEntries(
  allCountries.map((c) => {
    const englishDemonym = c.demonyms?.eng?.m ?? null; // or choose "f"
    const countryCode =
      c.idd?.root && c.idd?.suffixes?.length
        ? `${c.idd.root}${c.idd.suffixes[0]}`
        : "";

    return [
      c.cca2 as CountryCode,
      {
        name: c.name.common,
        flag: c.flag,
        countryCode,
        nationality: englishDemonym,
      },
    ];
  }),
);

export const PRIORITY_COUNTRY_CODES: CountryCode[] = ["NG", "CA"];
